import { useCallback, useEffect, useRef } from 'react';
import { FetchType } from '../../types/api/FetchType';
import { useGet } from '../../hooks/useGet';
import { FetchError } from '../../types/api/Block';
import noop from '../../lib/doNothing';
import { RefreshCallback } from './FetchedContainer';

type InstantContainer<Embedded> = {
  fetchUrl?: string;
  fetchType: FetchType.instant;
  embedded: Embedded;
};
type LazyContainer = {
  fetchUrl: string;
  fetchType: FetchType.lazy;
};

export type FetchableContainer<Embedded> = InstantContainer<Embedded> | LazyContainer;
type UseFetchableResult<Embedded> = {
  embedded?: Embedded | FetchError;
  refresh: RefreshCallback;
  refreshCount: number;
  requestError?: string;
  refreshProps: any;
  loading: boolean;
};

export function useFetchable<Embedded, Container extends FetchableContainer<Embedded>>(
  props: Container,
): UseFetchableResult<Embedded> {
  // useGet hook for fetching
  const { requestError, loading, response, get } = useGet<Embedded | FetchError>();

  const refreshCountRef = useRef(0);
  const isRefreshRef = useRef(false);
  const refreshPropsRef = useRef(undefined);

  const { current: refreshCount } = refreshCountRef;
  const { current: refreshProps } = refreshPropsRef;

  const refresh = useCallback(
    (t?: any) => {
      const abortFunction = props.fetchUrl ? get(props.fetchUrl, { delay: 500 }) : noop;
      // signal +1 on refreshcount after request is done !
      isRefreshRef.current = true;
      refreshPropsRef.current = t;
      return abortFunction;
    },
    [get, props.fetchUrl],
  );

  useEffect(() => {
    // increment count on loading, if it is triggered by refresh
    if (loading && isRefreshRef.current) {
      refreshCountRef.current += 1;
      isRefreshRef.current = false;
    }
  }, [loading]);

  // initial fetch for lazy components
  useEffect(() => {
    if (props.fetchType === FetchType.lazy) {
      return get(props.fetchUrl, { delay: 500 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialEmbedded = props.fetchType === FetchType.instant ? props.embedded : undefined;

  const embedded = response || initialEmbedded;

  return { refresh, refreshCount, refreshProps, requestError, loading, embedded };
}
